import { withJsonFormsControlProps } from "@jsonforms/react";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import "./DirectRewardsRedemption.scss";

export const DirectRewardsRedemption = (props) => {
  const { path, data, handleChange } = props;
  const [rewardWindow, setRewardWindow] = useState(data?.rewardWindow || 0);
  const [timeframe, setTimeframe] = useState(data?.timeframe || "hours");
  const [restrictExpiration, setRestrictExpiration] = useState(
    data?.restrictExpiration ?? true
  );
  const [error, setError] = useState("");

  const handleIncrement = () => {
    setRewardWindow((prev) => {
      const updated = prev + 1;
      validate(updated);
      return updated;
    });
  };

  const handleDecrement = () => {
    setRewardWindow((prev) => {
      const updated = Math.max(1, prev - 1); // Changed from 0 to 1 to prevent going below 1
      validate(updated);
      return updated;
    });
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    const updated = isNaN(value) ? 0 : value;
    setRewardWindow(updated);
    validate(updated);
  };

  const validate = (value) => {
    if (value <= 0) {
      setError("Reward expiration window must be greater than 0");
    } else {
      setError("");
    }
  };

  const updateSeconds = (value, unit) => {
    if (unit === "hours") {
      return value * 3600;
    } else if (unit === "days") {
      return value * 86400;
    }
    return 0;
  };

  useEffect(() => {
    const seconds = updateSeconds(rewardWindow, timeframe);
    // Update only the reward_expiration_window attribute with seconds
    handleChange("reward_expiration_window", seconds);
    handleChange(path, {
      timeframe,
      rewardWindow,
      restrictExpiration,
    });
  }, [rewardWindow, timeframe, path, handleChange, restrictExpiration]);

  return (
    <>
      <Box>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-start"
          gap={5}
          mb={2}
        >
          {/* Reward Expiration Section */}
          <Box display="flex" flexDirection="column">
            <Tooltip title="Time duration before the reward expires">
              <FormLabel
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontFamily: "Inter",
                  fontSize: "0.875rem",
                }}
              >
                Reward expiration window
                <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
              </FormLabel>
            </Tooltip>

            <Box display="flex" alignItems="center" mt={1}>
              <IconButton
                aria-label="decrement"
                onClick={handleDecrement}
                disabled={rewardWindow <= 1}
                sx={{
                  opacity: rewardWindow <= 1 ? 0.5 : 1,
                  border: "1px solid lightblue",
                  borderRight: "none",
                  borderRadius: "4px 0 0 4px",
                  backgroundColor: "#fff",
                }}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                value={rewardWindow}
                onChange={handleInputChange}
                type="number"
                size="small"
                InputProps={{ readOnly: true }}
                error={!!error}
                // helperText={error}
                inputProps={{ min: 0 }}
                sx={{
                  width: 60,
                  mx: 0,
                  border: "1px solid lightblue",
                  "& .MuiOutlinedInput-root": {
                    border: "none",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              />
              <IconButton
                aria-label="increment"
                onClick={handleIncrement}
                sx={{
                  border: "1px solid lightblue",
                  borderLeft: "none",
                  borderRadius: "0 4px 4px 0",
                  backgroundColor: "#fff",
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Timeframe Section (Side by Side) */}
          <FormControl component="fieldset">
            <Tooltip title="Choose whether the window is in hours or days">
              <FormLabel
                component="legend"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontFamily: "Inter",
                  fontSize: "0.875rem",
                  fontWeight: "400",
                  lineHeight: "140%",
                }}
              >
                Timeframe
                <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
              </FormLabel>
            </Tooltip>
            <RadioGroup
              row
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              sx={{
                marginTop: "8px",
              }}
            >
              <FormControlLabel
                value="hours"
                control={
                  <Radio
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 18,
                      },
                    }}
                  />
                }
                label="Hours"
                sx={{
                  fontFamily: "Inter",
                  fontSize: "0.875rem",
                  fontWeight: "400",
                  lineHeight: "140%",
                }}
              />
              <FormControlLabel
                value="days"
                control={
                  <Radio
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 18,
                      },
                    }}
                  />
                }
                label="Days"
                sx={{
                  fontFamily: "Inter",
                  fontSize: "0.875rem",
                  fontWeight: "400",
                  lineHeight: "140%",
                }}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={restrictExpiration}
              onChange={(e) => setRestrictExpiration(e.target.checked)}
            />
          }
          label={`Rewards are only valid during the campaign duration`}
          sx={{ mb: 3 }}
        />
      </Box>
    </>
  );
};

export const DirectRewardsRedemptionControl = withJsonFormsControlProps(
  DirectRewardsRedemption
);
